import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the mobile header drawer as a stack of sliding panes.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle - The hamburger toggle button.
 * @property {HTMLDialogElement} dialog - The <dialog> element hosting the panes.
 * @property {HTMLDivElement} viewport - The viewport containing the stacked panes.
 *
 * @extends {DialogComponent}
 */
class HeaderDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'toggle', 'viewport'];

  /** @type {string[]} */
  #stack = [];

  /** @type {HTMLElement | null} */
  #openerByPane = null;

  /** @type {Map<string, HTMLElement>} */
  #triggerForPane = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.addEventListener('click', this.#handleDelegatedClick);
    this.addEventListener('keydown', this.#handleKeyDown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.removeEventListener('click', this.#handleDelegatedClick);
    this.removeEventListener('keydown', this.#handleKeyDown, true);
  }

  /* --------------------------- Public API (on:click hooks) --------------------------- */

  /**
   * Opens the drawer (from the hamburger toggle).
   */
  open() {
    if (this.refs.dialog?.open) return;
    this.showDialog();
  }

  /**
   * Closes the drawer entirely (from the X button on any pane).
   */
  close() {
    this.closeDialog();
  }

  /* --------------------------- Internal lifecycle --------------------------- */

  #handleOpen = () => {
    this.setAttribute('data-drawer-open', 'true');
    this.refs.toggle?.setAttribute('aria-expanded', 'true');

    const rootPane = this.#getRootPane();
    if (!rootPane) return;

    this.#stack = [];
    this.#triggerForPane.clear();

    this.#activatePane(rootPane);
  };

  #handleClose = () => {
    this.removeAttribute('data-drawer-open');
    this.refs.toggle?.setAttribute('aria-expanded', 'false');

    removeTrapFocus();

    for (const pane of this.#panes) {
      pane.classList.remove('is-active');
      pane.setAttribute('aria-hidden', 'true');
    }

    for (const trigger of this.#triggerForPane.values()) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    const rootPane = this.#getRootPane();
    if (rootPane) {
      rootPane.classList.add('is-active');
      rootPane.setAttribute('aria-hidden', 'false');
    }

    this.#stack = [];
    this.#triggerForPane.clear();

    // Restore focus to the hamburger toggle.
    this.refs.toggle?.focus();
  };

  /* --------------------------- Delegated click / key handlers --------------------------- */

  /**
   * @param {MouseEvent} event
   */
  #handleDelegatedClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target : null);
    if (!target) return;

    const openTrigger = target.closest('[data-action="open-pane"]');
    if (openTrigger instanceof HTMLElement) {
      const paneId = openTrigger.getAttribute('data-target');
      if (paneId) {
        event.preventDefault();
        this.#openPane(paneId, openTrigger);
        return;
      }
    }

    const backTrigger = target.closest('[data-action="back"]');
    if (backTrigger instanceof HTMLElement) {
      event.preventDefault();
      this.#back();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.dialog?.open) return;

    // Preempt DialogComponent's default Escape -> close behavior when we want "back".
    if (this.#stack.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      this.#back();
    }
  };

  /* --------------------------- Pane stack management --------------------------- */

  /**
   * @param {string} paneId
   * @param {HTMLElement} trigger - The button that opened this pane.
   */
  #openPane(paneId, trigger) {
    const pane = this.#getPaneById(paneId);
    if (!pane) return;

    const currentPane = this.#getActivePane();

    this.#stack.push(paneId);
    this.#triggerForPane.set(paneId, trigger);
    trigger.setAttribute('aria-expanded', 'true');

    if (currentPane) {
      // Crossfade: the outgoing pane fades out while the incoming pane fades in.
      currentPane.classList.remove('is-active');
      currentPane.setAttribute('aria-hidden', 'true');
    }

    this.#activatePane(pane);
  }

  #back() {
    if (this.#stack.length === 0) {
      this.closeDialog();
      return;
    }

    const topPaneId = this.#stack.pop();
    const topPane = topPaneId ? this.#getPaneById(topPaneId) : null;
    const trigger = topPaneId ? this.#triggerForPane.get(topPaneId) : null;

    if (topPane) {
      topPane.classList.remove('is-active');
      topPane.setAttribute('aria-hidden', 'true');
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (topPaneId) this.#triggerForPane.delete(topPaneId);
    }

    // Activate the new top of the stack (or the root pane if stack is empty).
    const newTopPaneId = this.#stack[this.#stack.length - 1];
    const newTopPane = newTopPaneId ? this.#getPaneById(newTopPaneId) : this.#getRootPane();

    if (newTopPane) {
      this.#activatePane(newTopPane);
    }

    // Restore focus to the trigger that opened the pane we just closed.
    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }

  /**
   * @param {HTMLElement} pane
   */
  #activatePane(pane) {
    pane.classList.add('is-active');
    pane.setAttribute('aria-hidden', 'false');

    // Trap focus within the dialog (all panes scope, but only active pane is interactive visually).
    const { dialog } = this.refs;
    if (dialog instanceof HTMLElement) {
      // Move focus to the first focusable element inside the active pane.
      requestAnimationFrame(() => {
        const focusTarget = this.#getFirstFocusable(pane);
        if (focusTarget) {
          focusTarget.focus();
        } else {
          pane.setAttribute('tabindex', '-1');
          pane.focus();
        }
        trapFocus(dialog);
      });
    }
  }

  /* --------------------------- Helpers --------------------------- */

  /** @returns {HTMLElement[]} */
  get #panes() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.menu-drawer__pane')));
  }

  /**
   * @param {string} id
   * @returns {HTMLElement | null}
   */
  #getPaneById(id) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`.menu-drawer__pane[data-pane-id="${CSS.escape(id)}"]`));
  }

  /** @returns {HTMLElement | null} */
  #getRootPane() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.menu-drawer__pane[data-level="1"]'));
  }

  /** @returns {HTMLElement | null} */
  #getActivePane() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.menu-drawer__pane.is-active'));
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  #getFirstFocusable(root) {
    const selector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
    const candidates = Array.from(root.querySelectorAll(selector));
    return /** @type {HTMLElement | null} */ (candidates[0] ?? null);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
